import { Component, OnInit, ViewContainerRef } from '@angular/core';
import { MatDialog, MatDialogConfig } from '@angular/material/dialog';
import { Subscription } from 'rxjs';
import { Sort } from '@angular/material/sort';
import * as _ from 'lodash';
import { ICompanySetting, ISetting, ISettingFilter } from '../settings.interface';
import { SettingsService } from '../settings.service';
import { Router } from '@angular/router';
import { SettingsCompanyModalComponent } from '../settings-company-modal/settings-company-modal.component';
import { ICompany } from '../../../companies-page/companies/companies.interface';
import { Broadcaster } from '../../../../shared/components/broadcaster.component';
import { DialogService } from '../../../../shared/services/dialog/dialog.service';
import { AuthService } from '../../../../shared/services/auth.service';
import { IConfirmationDialogOptions } from '../../../../shared/services/dialog/confirmation-dialog/confirmation-dialog-options.interface';
import { IInformationDialogOptions } from '../../../../shared/services/dialog/information-dialog/information-dialog-options.interface';

let self: any;

@Component({
  selector: 'app-settings-company',
  templateUrl: './settings-company.component.html',
  styleUrls: ['./settings-company.component.css']
})
export class SettingsCompanyComponent implements OnInit {
  settings: ISetting[] = [];
  loading: boolean = false;
  thereAreMore: boolean = false;
  filter: ISettingFilter = {
    skip: 0,
    limit: 20
  };
  filtered = false;
  scrollSubscription!: Subscription;
  sortOptions: { [key: string]: string } = {
    code: 'Código',
    '-code': 'Código Descendente',
    name: 'Nombre',
    '-name': 'Nombre Descendente'
  };
  sortOptionsKeys = Object.keys(this.sortOptions);
  selection: any = { all: false };
  isDev: any;
  company!: ICompany;

  constructor(
    private _SettingsService: SettingsService,
    private _broadcaster: Broadcaster,
    private _dialogService: DialogService,
    private _dialog: MatDialog,
    private _viewContainerRef: ViewContainerRef,
    private router: Router,
    private _AuthService: AuthService
  ) {
    self = this;
    this.registerStringBroadcast();
  }

  ngOnInit() {
    this.company = this._AuthService.company();
    this.filter.company = this.company._id;
    this.isDev = this._AuthService.isRole('dev');
    this.getCompanySettings(true);
  }

  getCompanySettings(reset?: boolean) {
    this.loading = true;
    this.thereAreMore = false;
    if (reset) {
      window.scroll(0, 0);
      this.filter.skip = 0;
      this.settings = [];
      this.clearSelection();
    }
    this._SettingsService.getCompanySettings(this.filter).subscribe((settings: any) => {
      this.settings = _.concat(this.settings, settings);
      const filter: any = this.filter;
      this.thereAreMore = settings.length >= filter.limit;
      this.loading = false;
    })
  }

  loadMore() {
    if (this.thereAreMore && !this.loading) {
      const filter: any = this.filter;
      filter.skip += filter.limit;
      this.getCompanySettings();
    }
  }

  registerStringBroadcast() {
    this.scrollSubscription = this._broadcaster.on<string>('FinishScrollDown')
      .subscribe(() => {
        this.loadMore();
      });
  }

  ngOnDestroy() {
    this.scrollSubscription.unsubscribe();
  }

  sortSettings(sort: Sort | string) {
    this.filter.sort = (typeof sort == 'string') ? sort : ((sort.direction == 'asc') ? '' : '-') + sort.active;
    this.getCompanySettings(true);
  }

  openFilter() {
    console.log('openFilter');
  }

  openNewSettingModal() {
    this.openSettingModal({});
  }

  openSettingModal(setting: ISetting, readOnly: boolean = false) {
    let config = new MatDialogConfig();
    // config.disableClose = true;
    config.width = '400px';
    config.maxWidth = '90vw';
    config.viewContainerRef = this._viewContainerRef;
    self.dialogRef = this._dialog.open(SettingsCompanyModalComponent, config);
    self.dialogRef.componentInstance.setting = setting;
    self.dialogRef.componentInstance.readOnly = readOnly;

    self.dialogRef.afterClosed().subscribe((result: any) => {
      self.dialogRef = null;
      if (result) this.getCompanySettings(true);
    });
  }

  openDeleteSettingConfirm(setting: ISetting, hardDelete: boolean = false) {
    const modalOptions: IConfirmationDialogOptions = {
      title: "Eliminar Setting",
      message: "Esta seguro que desea eliminar '" + setting.name + "'?",
      yesButtonLabel: 'Aceptar', noButtonLabel: 'Cancelar'
    };
    this._dialogService.openConfirmationModal(modalOptions, this._viewContainerRef)
      .subscribe((confirmation: any) => {
        if (confirmation) {
          this._SettingsService.deleteSetting(setting, hardDelete).subscribe(() => {
            if (hardDelete) this.getCompanySettings(true);
            else setting.isDeleted = true;
          })
        }
      });
  }

  openRestoreSettingConfirm(setting: ISetting) {
    const modalOptions: IConfirmationDialogOptions = {
      title: "Restaurar Setting",
      message: "Esta seguro que desea restaurar '" + setting.name + "'?",
      yesButtonLabel: 'Aceptar', noButtonLabel: 'Cancelar'
    };
    this._dialogService.openConfirmationModal(modalOptions, this._viewContainerRef)
      .subscribe((confirmation: any) => {
        if (confirmation) {
          this._SettingsService.restoreSetting(setting).subscribe(() => {
            setting.isDeleted = false;
          })
        }
      });
  }

  clearSelection() {
    this.selection = { all: false }
  }

  checkSomeSelected() {
    this.selection.some = Object.keys(this.selection)
      .filter(el => el !== 'all' && el !== 'some')
      .filter(el => this.selection[el])
      .length > 0;
  }
  
  toggleSelection(_id?: string) {
    if (_id) {
      // Modificar la selección de un elemento individual
      this.selection[_id] = !this.selection[_id];

      // Verificar si todos los elementos están seleccionados
      const allSelected = this.settings.every(setting => {
        const id = setting?._id || '';
        return this.selection[id];
      });

      // Actualizar "seleccionar todos" en base a los elementos seleccionados
      this.selection.all = allSelected;
    } else {
      // Seleccionar o deseleccionar todos
      const newValue = !this.selection.all;
      this.selection = { all: newValue };

      // Aplicar el nuevo estado a todos los elementos
      this.settings.forEach(setting => {
        const id = setting?._id || '';
        this.selection[id] = newValue;
      });
    }

    // Actualizar si hay alguna selección
    this.checkSomeSelected();
  }

  openSettings() {
    this.router.navigate(['/admin', this._AuthService.companyIndex(), 'settings']);
  }

  openSetDefaultValueSettingConfirm(setting: ISetting) {
    const modalOptions: IConfirmationDialogOptions = {
      title: "Restaurar por Defecto",
      message: "Esta seguro que desa restaurar el valor por defecto de esta opcion?",
      yesButtonLabel: "Aceptar",
      noButtonLabel: "Cancelar"
    };
    this._dialogService.openConfirmationModal(modalOptions, this._viewContainerRef)
      .subscribe((response: any) => {
        if (response) {
          const data: ICompanySetting = {
            _id: _.get(setting, 'companySetting._id'),
            company: _.get(setting, 'companySetting.company', this.company._id),
            setting: _.get(setting, 'companySetting.setting', setting._id),
          };
          data.value = setting.defaultValue;
          // console.log(setting, data);
          this._SettingsService.setCompanySetting(data)
            .subscribe(() => {
              this.getCompanySettings(true);
            })
        }
      })
  }

  openUnderConstructionDialog() {
    const modalOptions: IInformationDialogOptions = {
      title: "¡Próximamente!",
      message: "Esta funcionalidad se encuentra en construcción.",
      closeButtonLabel: "Ok"
    };
    this._dialogService.openInformationModal(modalOptions, this._viewContainerRef)
      .subscribe(() => {
        console.log('ok')
      });
  }
}